import type * as SentryNS from '@sentry/react'

import { shouldInitSentry } from './sentryGate'

/**
 * T2899 — Sentry se naloži PO prvem izrisu (odločitev Alena 22.10.2026).
 *
 * `@sentry/react` (kos `vendor-sentry`, ~103 kB gzip) je bil do te naloge v
 * vstopnem grafu, čeprav ga prvi zaslon ne potrebuje. Ta modul je edina
 * vstopna točka do Sentryja v vstopnem grafu in ga NE uvozi (samo tipe):
 *
 *  - `installEarlyErrorCapture()` (iz `sentryEarly.ts`, prvi uvoz v `main.tsx`)
 *    priklopi lahka poslušalca `error` in `unhandledrejection` na `window`;
 *    napake pred nalaganjem gredo v omejeno vrsto (`EARLY_QUEUE_LIMIT`),
 *  - `scheduleSentryStart()` po prvem izrisu ob nedejavnosti brskalnika
 *    (`requestIdleCallback`, sicer `setTimeout`) naloži `sentryInit.ts`,
 *  - prva zgodnja napaka nalaganje sproži TAKOJ (ne čaka nedejavnosti),
 *  - po `Sentry.init` se vrsta pošlje, poslušalca se odklopita (od tu napake
 *    lovi Sentry sam, `globalHandlersIntegration`), `captureException` pa gre
 *    neposredno v Sentry.
 *
 * Brez DSN ali v razvoju brez `VITE_SENTRY_DEV_ENABLED` (T2026) se ne naloži
 * nič in vrsta ostane prazna.
 */
export type SentryModule = typeof SentryNS

type CaptureContext = Parameters<SentryModule['captureException']>[1]

interface QueuedError {
  error: unknown
  context?: CaptureContext
}

/** Največ toliko zgodnjih napak čaka na Sentry; ostale se zavržejo (zanka napak). */
export const EARLY_QUEUE_LIMIT = 20

let enabled = false
let loaded: SentryModule | null = null
let startPromise: Promise<SentryModule | null> | null = null
const queue: QueuedError[] = []
let listening = false

function enqueue(item: QueuedError): void {
  if (queue.length < EARLY_QUEUE_LIMIT) queue.push(item)
  // Napaka pred nalaganjem: poročanje naložimo takoj, ne šele ob nedejavnosti.
  void startSentry()
}

function onWindowError(event: ErrorEvent): void {
  enqueue({
    error: event.error ?? new Error(event.message || 'Unknown error'),
    context: { tags: { early_capture: 'error' } },
  })
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  enqueue({ error: event.reason, context: { tags: { early_capture: 'unhandledrejection' } } })
}

function stopListening(): void {
  if (!listening) return
  window.removeEventListener('error', onWindowError)
  window.removeEventListener('unhandledrejection', onUnhandledRejection)
  listening = false
}

/** Vklopi Sentry (po vratih T2026) in priklopi zgodnji lovilec napak. */
export function installEarlyErrorCapture(
  isEnabled: boolean = shouldInitSentry(import.meta.env),
): void {
  enabled = isEnabled
  if (!enabled || listening || loaded) return
  window.addEventListener('error', onWindowError)
  window.addEventListener('unhandledrejection', onUnhandledRejection)
  listening = true
}

/**
 * Naloži in inicializira Sentry (enkrat). Razreši se z modulom ali `null`,
 * ko je Sentry izklopljen ali se kos ni naložil.
 */
export function startSentry(): Promise<SentryModule | null> {
  if (!enabled) return Promise.resolve(null)
  startPromise ??= import('./sentryInit')
    .then(async ({ initSentry, afterSentryInit }) => {
      const Sentry = await initSentry()
      loaded = Sentry
      stopListening()
      afterSentryInit()
      for (const item of queue.splice(0)) Sentry.captureException(item.error, item.context)
      return Sentry
    })
    .catch((err: unknown) => {
      // Kos se ni naložil (npr. omrežje): aplikacija teče brez poročanja.
      console.error('[Sentry] Nalaganje ni uspelo:', err)
      return null
    })
  return startPromise
}

/** Po prvem izrisu: naloži Sentry, ko je brskalnik nedejaven. */
export function scheduleSentryStart(): void {
  if (!enabled) return
  const run = () => void startSentry()
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 4000 })
  } else {
    setTimeout(run, 1500)
  }
}

/** Sentry, če je že naložen in inicializiran; sicer `null`. */
export function getLoadedSentry(): SentryModule | null {
  return loaded
}

/**
 * Poroča napako: takoj, če je Sentry naložen, sicer v vrsto (in sproži
 * nalaganje). Uporablja ga `AppErrorBoundary`.
 */
export function captureException(error: unknown, context?: CaptureContext): void {
  if (loaded) {
    loaded.captureException(error, context)
    return
  }
  if (enabled) enqueue({ error, context })
}

/** Samo za teste: podtakne (mock) modul kot naložen Sentry. */
export function __setLoadedSentryForTests(mod: SentryModule | null): void {
  loaded = mod
}

/** Samo za teste: ponastavi stanje modula. */
export function __resetSentryLazyForTests(): void {
  stopListening()
  enabled = false
  loaded = null
  startPromise = null
  queue.length = 0
}

/** Samo za teste: trenutna dolžina vrste. */
export function __earlyQueueLengthForTests(): number {
  return queue.length
}
